/**
 * Multi-rack Summary display (#995) — per-unit rows aligned with the Racks step editor.
 */

import { RACK_FORM_SUMMARY_FIELD_ORDER } from '../request/fields/requestFieldRegistry';
import {
  getRacksDescriptorSummaryDefinitions,
  getRacksDescriptorUnitTitle,
  racksDescriptorUnitRowValue,
} from '../request/sections/racksDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { RackConfiguration } from '../types/rack';

/**
 * Summary rows for one rack unit — the descriptor-engine source (Story 3.3).
 * Byte-identical to the pre-engine bespoke row set (parity oracle
 * `racks-summary-rows.json`).
 */
export function getPerRackSummaryDefinitions(): SummaryCardRow[] {
  return getRacksDescriptorSummaryDefinitions();
}

export function resolveSummaryRackConfigurations(input: {
  sessionRacks?: RackConfiguration[] | null;
  draftRacks?: RackConfiguration[] | null;
  persistedRacks?: RackConfiguration[] | null;
  /**
   * #997 C3: true when the section was edited THIS session (dirty). When set, the
   * edited/draft array is authoritative even when EMPTY — so deleting every unit
   * does not resurrect the persisted query rows in the Summary before save.
   * Mirrors the step-layout `effectiveUnits` dirty guard. Defaults to prior behavior.
   */
  edited?: boolean;
}): RackConfiguration[] {
  const { sessionRacks, draftRacks, persistedRacks, edited } = input;
  if (sessionRacks && sessionRacks.length > 0) {
    return sessionRacks;
  }
  if (edited) {
    return draftRacks ?? [];
  }
  if (draftRacks && draftRacks.length > 0) {
    return draftRacks;
  }
  if (persistedRacks && persistedRacks.length > 0) {
    return persistedRacks;
  }
  return [];
}

export function getRackSummaryUnitTitle(
  rack: RackConfiguration,
  index: number,
): string {
  return getRacksDescriptorUnitTitle(rack, index);
}

export function rackSummaryUnitRowValue(
  rack: RackConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return racksDescriptorUnitRowValue(rack, dataField);
}

const RACK_SUMMARY_COUNT_FIELDS = RACK_FORM_SUMMARY_FIELD_ORDER.map(
  ({ dataField, shortLabel }) => ({
    key: dataField,
    shortLabel: shortLabel ?? dataField,
  }),
);

/** Same fill rules as rack editor subsection badges (`subsectionHeaderMetrics`). */
export function countFilledRackSummaryFields(
  rack: RackConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getRacksDescriptorSummaryDefinitions()).map(row => row.dataField),
  );
  const fields = RACK_SUMMARY_COUNT_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    rack as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledRackSummaryFieldsForRacks(
  racks: readonly RackConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return racks.reduce(
    (sum, rack) => sum + countFilledRackSummaryFields(rack, rows),
    0,
  );
}
